import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ListOrdered, Lock } from 'lucide-react';

import { ErroApi } from '@/nucleo/auth/cliente-api';
import { DataPagination } from '@/ui/DataPagination';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { PortalHero } from '@/ui/PortalHero';
import { Spinner } from '@/ui/Spinner';
import { StatusBadge } from '@/ui/StatusBadge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/base/table';

import { CLASSE_DO_SELECT } from '@/modulos/operacional/inscricoes/estilos';

import { capaDaPagina } from '../imagensDeCapa';

import { HashCopiavel } from './componentes';
import {
  chaveDoEstrato,
  data,
  dataHora,
  ROTULO_STATUS_SORTEIO,
  rotuloModalidade,
  rotuloSituacao,
  VARIANTE_STATUS_SORTEIO,
  varianteSituacao,
  type FiltroDaClassificacao,
  type SorteioPublico,
} from './contrato';
import { useClassificacaoPublica, useSorteioPublico } from './dados';
import { VerifiqueVoceMesmo } from './VerifiqueVoceMesmo';

function Dado({ rotulo, children }: { rotulo: string; children: ReactNode }): ReactElement {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground text-xs uppercase tracking-wide">{rotulo}</dt>
      <dd className="wrap-break-word text-sm font-medium">{children}</dd>
    </div>
  );
}

/** Classificação pública, paginada no servidor, sem placa (D-07). */
function Classificacao({ s }: { s: SorteioPublico }): ReactElement {
  const [filtro, setFiltro] = useState<FiltroDaClassificacao>({
    estrato: '',
    situacao: '',
    texto: '',
  });
  const [texto, setTexto] = useState('');
  const [pagina, setPagina] = useState(1);
  const [tamanho, setTamanho] = useState(25);
  const consulta = useClassificacaoPublica(s.id, filtro, pagina, tamanho, true);

  // Busca por texto: espera a pessoa parar de digitar.
  useEffect(() => {
    if (texto === filtro.texto) return undefined;
    const t = setTimeout(() => {
      setFiltro((f) => ({ ...f, texto }));
      setPagina(1);
    }, 400);
    return () => clearTimeout(t);
  }, [texto, filtro.texto]);

  const linhas = consulta.data?.itens ?? [];
  const total = consulta.data?.total ?? 0;

  return (
    <section aria-labelledby="titulo-classificacao" className="space-y-4">
      <h2 id="titulo-classificacao" className="text-xl font-bold tracking-tight">
        Classificação
      </h2>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor="publico-estrato">Estrato (DRE e modalidade)</Label>
          <select
            id="publico-estrato"
            className={CLASSE_DO_SELECT}
            value={filtro.estrato}
            onChange={(e) => {
              setFiltro((f) => ({ ...f, estrato: e.target.value }));
              setPagina(1);
            }}
          >
            <option value="">Todos os estratos</option>
            {s.estratos.map((e) => (
              <option
                key={chaveDoEstrato(e.dreCodigo, e.modalidade)}
                value={chaveDoEstrato(e.dreId ?? '', e.modalidade)}
              >
                {e.dreNome} · {rotuloModalidade(e.modalidade)}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="publico-situacao">Situação</Label>
          <select
            id="publico-situacao"
            className={CLASSE_DO_SELECT}
            value={filtro.situacao}
            onChange={(e) => {
              setFiltro((f) => ({
                ...f,
                situacao: e.target.value as FiltroDaClassificacao['situacao'],
              }));
              setPagina(1);
            }}
          >
            <option value="">Todas</option>
            <option value="CONTEMPLADO">Contemplados</option>
            <option value="SUPLENTE">Suplentes</option>
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="publico-busca">Buscar protocolo ou nome</Label>
          <Input
            id="publico-busca"
            type="search"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
        </div>
      </div>

      {consulta.isPending ? (
        <Spinner size="lg" rotulo="Carregando a classificação" />
      ) : consulta.isError ? (
        <ErrorState
          variant="load-failed"
          title="Não foi possível carregar a classificação"
          description="Verifique sua conexão e tente de novo em instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void consulta.refetch() }}
        />
      ) : linhas.length === 0 ? (
        <EmptyState
          variant="search"
          icon={ListOrdered}
          title="Ninguém nesta seleção"
          description="Troque o estrato, a situação ou a busca."
        />
      ) : (
        <>
          <Table aria-label="Classificação do sorteio">
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Posição</TableHead>
                <TableHead>Protocolo</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead className="hidden md:table-cell">Estrato</TableHead>
                <TableHead>Situação</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {linhas.map((l) => (
                <TableRow key={`${l.dreCodigo}|${l.modalidade}|${l.protocolo}`}>
                  <TableCell className="tabular-nums">{l.posicao}º</TableCell>
                  <TableCell className="font-mono text-xs">{l.protocolo}</TableCell>
                  <TableCell className="wrap-break-word">{l.nome}</TableCell>
                  <TableCell className="hidden md:table-cell">
                    {l.dreNome} · {rotuloModalidade(l.modalidade)}
                  </TableCell>
                  <TableCell>
                    <StatusBadge
                      status={varianteSituacao(l.situacao, l.chamadoEm)}
                      label={rotuloSituacao(l.situacao, l.posicaoEspera)}
                    />
                    {l.chamadoEm && (
                      <span className="text-muted-foreground block text-xs">
                        Chamado em {data(l.chamadoEm)}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {total > tamanho && (
            <DataPagination
              page={pagina}
              perPage={tamanho}
              total={total}
              onPageChange={setPagina}
              onPerPageChange={(t) => {
                setTamanho(t);
                setPagina(1);
              }}
            />
          )}
        </>
      )}
    </section>
  );
}

function Conteudo({ s }: { s: SorteioPublico }): ReactElement {
  const executado = s.status !== 'PREPARADO';
  useEffect(() => {
    document.title = `Sorteio ${s.cicloNome} — Sistema TEG`;
  }, [s.cicloNome]);

  return (
    <div>
      <PortalHero
        image={capaDaPagina('dados-publicos')}
        title={`Sorteio — ${s.cicloNome}`}
        description="Resultado público do sorteio do credenciamento, por estrato (DRE e modalidade), e os dados para conferir que ele não foi alterado."
        compact
      />
      <div className="mx-auto w-full max-w-5xl space-y-8 px-4 py-10 sm:px-6">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/dados-publicos/sorteios">
            <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" />
            Todos os sorteios
          </Link>
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center gap-2 text-base">
              Situação
              <StatusBadge
                status={VARIANTE_STATUS_SORTEIO[s.status]}
                label={ROTULO_STATUS_SORTEIO[s.status]}
              />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Dado rotulo="Participantes">{s.totalParticipantes}</Dado>
              <Dado rotulo="Vagas">{s.totalVagas}</Dado>
              <Dado rotulo="Preparado em">{dataHora(s.preparadoEm)}</Dado>
              <Dado rotulo="Executado em">{dataHora(s.executadoEm)}</Dado>
              <Dado rotulo="Publicado em">{dataHora(s.publicadoEm)}</Dado>
              <Dado rotulo="Publicação oficial">{s.publicacaoReferencia ?? '—'}</Dado>
            </dl>
          </CardContent>
        </Card>

        {!executado && (
          <section
            aria-labelledby="titulo-compromisso"
            className="bg-muted/40 space-y-3 rounded-lg border p-4"
          >
            <h2 id="titulo-compromisso" className="flex items-center gap-2 text-lg font-semibold">
              <Lock className="h-4 w-4" aria-hidden="true" />
              Compromisso registrado
            </h2>
            <p className="text-sm leading-relaxed">
              A lista de participantes e as vagas estão congeladas, e o sistema já registrou o hash
              da semente que vai decidir o sorteio. O resultado sai na execução: nesse momento a
              semente é revelada, e qualquer pessoa confere que ela é a mesma deste compromisso.
            </p>
            <dl className="grid gap-3">
              <Dado rotulo="Hash do universo (lista de participantes)">
                <HashCopiavel valor={s.universoHash} rotulo="hash do universo" />
              </Dado>
              <Dado rotulo="Hash da semente (compromisso)">
                <HashCopiavel valor={s.sementeHash} rotulo="hash da semente" />
              </Dado>
            </dl>
          </section>
        )}

        <section aria-labelledby="titulo-estratos" className="space-y-3">
          <h2 id="titulo-estratos" className="text-xl font-bold tracking-tight">
            Estratos
          </h2>
          {s.estratos.length === 0 ? (
            <EmptyState icon={ListOrdered} title="Nenhum estrato neste sorteio" />
          ) : (
            <Table aria-label="Estratos do sorteio">
              <TableHeader>
                <TableRow>
                  <TableHead>DRE</TableHead>
                  <TableHead>Modalidade</TableHead>
                  <TableHead className="text-right">Vagas</TableHead>
                  <TableHead className="text-right">Participantes</TableHead>
                  {executado && <TableHead className="text-right">Contemplados</TableHead>}
                  {executado && <TableHead className="text-right">Suplentes</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {s.estratos.map((e) => (
                  <TableRow key={chaveDoEstrato(e.dreCodigo, e.modalidade)}>
                    <TableCell>{e.dreNome}</TableCell>
                    <TableCell>{rotuloModalidade(e.modalidade)}</TableCell>
                    <TableCell className="text-right tabular-nums">{e.vagas}</TableCell>
                    <TableCell className="text-right tabular-nums">{e.participantes}</TableCell>
                    {executado && (
                      <TableCell className="text-right tabular-nums">{e.contemplados}</TableCell>
                    )}
                    {executado && (
                      <TableCell className="text-right tabular-nums">{e.suplentes}</TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </section>

        {executado && <Classificacao s={s} />}

        {executado && (
          <section aria-labelledby="titulo-chamadas" className="space-y-3">
            <h2 id="titulo-chamadas" className="text-xl font-bold tracking-tight">
              Suplentes chamados
            </h2>
            {s.chamadas.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                Nenhum suplente foi chamado até agora. Quando uma vaga de contemplado se abre, o
                próximo suplente do mesmo estrato é chamado, na ordem, e aparece aqui.
              </p>
            ) : (
              <ul className="space-y-2">
                {s.chamadas.map((c) => (
                  <li key={`${c.protocolo}-${c.em}`} className="border-l-2 pl-3 text-sm">
                    <span className="font-mono">{c.protocolo}</span>, suplente nº {c.posicaoEspera}{' '}
                    de {c.dreNome} · {rotuloModalidade(c.modalidade)}
                    <span className="text-muted-foreground block text-xs">
                      Chamado em {dataHora(c.em)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <VerifiqueVoceMesmo s={s} />
      </div>
    </div>
  );
}

/**
 * Resultado público de um sorteio — `/dados-publicos/sorteios/:id`.
 *
 * Aberto a qualquer pessoa (D-07): estratos, classificação com o nome da
 * entidade (sem placa), suplentes chamados e a verificação. Antes da execução,
 * mostra o compromisso (hashes) e explica que o resultado sai na execução.
 */
export function SorteioPublicoPage(): ReactElement {
  const { id = '' } = useParams();
  const consulta = useSorteioPublico(id);
  const naoExiste = consulta.error instanceof ErroApi && consulta.error.situacao === 404;
  const conteudo = consulta.data;

  if (consulta.isPending) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 py-16 sm:px-6">
        <Spinner size="lg" rotulo="Carregando o sorteio" />
      </div>
    );
  }
  if (naoExiste) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 py-16 sm:px-6">
        <ErrorState
          variant="resource-not-found"
          title="Sorteio não encontrado"
          description="Confira o endereço, ou veja a lista de sorteios publicados."
          primaryAction={{ label: 'Ver os sorteios', to: '/dados-publicos/sorteios' }}
        />
      </div>
    );
  }
  if (consulta.isError || !conteudo) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 py-16 sm:px-6">
        <ErrorState
          variant="load-failed"
          title="Não foi possível carregar o sorteio"
          description="Verifique sua conexão e tente de novo em instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void consulta.refetch() }}
        />
      </div>
    );
  }
  return <Conteudo s={conteudo} />;
}

export default SorteioPublicoPage;
